import { create } from 'zustand'
import { api } from '../services/api'

export interface MCPTool {
  name: string
  description: string
  inputSchema: {
    type: 'object'
    properties: Record<string, unknown>
    required?: string[]
  }
}

export interface MCPServer {
  id: string
  userId: string
  name: string
  command: string
  args: string[]
  env: Record<string, string>
  description?: string
  enabled: boolean
  status: 'disconnected' | 'connecting' | 'connected' | 'error'
  lastError?: string
  tools?: MCPTool[]
  isConnected?: boolean
  connectionError?: string | null
  createdAt: string
  updatedAt: string
}

interface MCPStore {
  servers: MCPServer[]
  selectedServer: MCPServer | null
  allTools: Array<{
    serverId: string
    serverName: string
    tools: MCPTool[]
  }>
  isLoading: boolean
  error: string | null

  // Actions
  fetchServers: () => Promise<void>
  fetchServer: (id: string) => Promise<MCPServer | null>
  createServer: (data: Partial<MCPServer>) => Promise<MCPServer>
  updateServer: (id: string, data: Partial<MCPServer>) => Promise<MCPServer>
  deleteServer: (id: string) => Promise<void>
  startServer: (id: string) => Promise<void>
  stopServer: (id: string) => Promise<void>
  fetchTools: (serverId: string) => Promise<MCPTool[]>
  callTool: (serverId: string, toolName: string, args: Record<string, unknown>) => Promise<unknown>
  fetchAllTools: () => Promise<void>
  selectServer: (server: MCPServer | null) => void
  clearError: () => void
}

export const useMCPStore = create<MCPStore>((set, get) => ({
  servers: [],
  selectedServer: null,
  allTools: [],
  isLoading: false,
  error: null,

  fetchServers: async () => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.get('/mcp/servers')
      set({ servers: response.data, isLoading: false })
    } catch (error) {
      set({ error: 'Failed to fetch MCP servers', isLoading: false })
    }
  },

  fetchServer: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.get(`/mcp/servers/${id}`)
      set({ isLoading: false })
      return response.data
    } catch (error) {
      set({ error: 'Failed to fetch MCP server', isLoading: false })
      return null
    }
  },

  createServer: async (data: Partial<MCPServer>) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.post('/mcp/servers', data)
      set(state => ({
        servers: [response.data, ...state.servers],
        isLoading: false
      }))
      return response.data
    } catch (error) {
      set({ error: 'Failed to create MCP server', isLoading: false })
      throw error
    }
  },

  updateServer: async (id: string, data: Partial<MCPServer>) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.put(`/mcp/servers/${id}`, data)
      set(state => ({
        servers: state.servers.map(s => s.id === id ? response.data : s),
        selectedServer: state.selectedServer?.id === id ? response.data : state.selectedServer,
        isLoading: false
      }))
      return response.data
    } catch (error) {
      set({ error: 'Failed to update MCP server', isLoading: false })
      throw error
    }
  },

  deleteServer: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      await api.delete(`/mcp/servers/${id}`)
      set(state => ({
        servers: state.servers.filter(s => s.id !== id),
        selectedServer: state.selectedServer?.id === id ? null : state.selectedServer,
        isLoading: false
      }))
    } catch (error) {
      set({ error: 'Failed to delete MCP server', isLoading: false })
      throw error
    }
  },

  startServer: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      await api.post(`/mcp/servers/${id}/start`)
      await get().fetchServers()
    } catch (error) {
      set({ error: 'Failed to start MCP server', isLoading: false })
      throw error
    }
  },

  stopServer: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      await api.post(`/mcp/servers/${id}/stop`)
      await get().fetchServers()
    } catch (error) {
      set({ error: 'Failed to stop MCP server', isLoading: false })
      throw error
    }
  },

  fetchTools: async (serverId: string) => {
    try {
      const response = await api.get(`/mcp/servers/${serverId}/tools`)
      return response.data
    } catch (error) {
      set({ error: 'Failed to fetch MCP tools' })
      return []
    }
  },

  callTool: async (serverId: string, toolName: string, args: Record<string, unknown>) => {
    try {
      const response = await api.post(`/mcp/servers/${serverId}/tools/${toolName}/call`, { arguments: args })
      return response.data
    } catch (error) {
      set({ error: 'Failed to call MCP tool' })
      throw error
    }
  },

  fetchAllTools: async () => {
    try {
      const response = await api.get('/mcp/tools')
      set({ allTools: response.data })
    } catch (error) {
      set({ error: 'Failed to fetch all tools' })
    }
  },

  selectServer: (server: MCPServer | null) => {
    set({ selectedServer: server })
  },

  clearError: () => {
    set({ error: null })
  }
}))
